/*
 * Carbisdale - Overlay-Modul
 *
 * Verwaltet einen temporären Layer über dem Screen für Menüs und Dialoge.
 * Der Overlay überschreibt nur die Positionen, an denen er Zeichen hat.
 * Leere Positionen zeigen den darunterliegenden Screen-Inhalt.
 *
 * Verantwortlichkeiten:
 * - Sparse Grid verwalten (nur gesetzte Positionen speichern)
 * - Box-Rahmen zeichnen
 * - Text in Boxen schreiben
 * - Overlay leeren (schließen)
 */

import { Style, createScreenCharacter, EDITOR } from './config.js'
import { computeStringLayout } from './GlyphMetrics.js'

/**
 * Overlay-Klasse
 *
 * Ein Sparse-Layer der über dem Screen gerendert wird.
 * Speichert nur Positionen die tatsächlich Inhalt haben.
 */
export class Overlay {
    /**
     * Erstellt einen neuen Overlay.
     *
     * @param {number} cols - Maximale Spaltenanzahl (Screen-Breite)
     * @param {number} rows - Maximale Zeilenanzahl (Screen-Höhe)
     */
    constructor(cols, rows) {
        this.cols = cols
        this.rows = rows

        // Sparse Map: key = "x,y", value = ScreenCharacter
        this.cells = new Map()

        // Dirty-Flag für Rendering
        this.isDirty = false

        // Ist der Overlay aktiv/sichtbar?
        this.isActive = false

        // Proportionale Text-Spans (werden über Grid-Zellen gerendert)
        this.textSpans = []

        // Frames: Menü/Dialog-Rahmen mit Background, Border (Path) und
        // optionalem Title-Strip. Der Renderer zeichnet sie vor Cells/Spans.
        // Phase 1 — siehe docs/menu-inventory-and-concept.md §4.
        this.frames = []

        // Selection-Stripes: vollflaechige Hintergrund-Rects fuer das aktuell
        // selektierte Item, gerendert nach den Frames und vor den Cells/Spans.
        this.stripes = []

        // Circular Control-Icons: pixel-praezise platzierte Kreis-Icons im
        // Prop-Mode (z.B. Context-Menu Fold/Unfold/Break/Out/In/Lock). Werden
        // nach Text-Spans gerendert, damit sie als visueller Akzent obenauf
        // liegen. Mono nutzt stattdessen Text-Praefixe (kein icons-Eintrag).
        this.icons = []

        // Phosphor-Overlay (Gimmick fuer About-Box): wenn gesetzt, rendert der
        // Renderer eine mode-unabhaengige Box mit schwarzem BG + grueem Glow,
        // unabhaengig vom aktuellen DisplayMode. Bypassed das Style-System.
        this.phosphorOverlay = null

        // Wird von deactivate() gesetzt — signalisiert dem Render-Loop, dass
        // das Overlay potenziell ueber Pane-Borders hinweg gemalt hat und der
        // Border-Layer neu gezeichnet werden muss. Die innerBounds der Panes
        // werden pro Frame ohnehin gewischt; persistente Border-Bereiche
        // dagegen nur bei `layout.borderDirty`. Ohne dieses Flag blieben
        // Menue-Reste auf den Border-Linien stehen.
        this.borderMayBeDirty = false

        // Geometry-Cache pro Modal-Key fuer markBorderDirtyIfModalBoundsChanged.
        // Modals (LinkPicker, PathPicker, Dashboard-ContextMenu, Drag-Ghost)
        // re-rendern mehrmals pro Sekunde (Cursor-Blink, Hover). Border-Layer
        // muss nur neu, wenn sich die Modal-Geometry geaendert hat.
        this._lastModalBounds = new Map()
    }

    /**
     * Prüft ob an einer Position ein Zeichen ist.
     *
     * @param {number} x - Spalte
     * @param {number} y - Zeile
     * @returns {boolean}
     */
    hasCharAt(x, y) {
        return this.cells.has(`${x},${y}`)
    }

    /**
     * Liest das Zeichen an einer Position.
     *
     * @param {number} x - Spalte
     * @param {number} y - Zeile
     * @returns {Object|null} ScreenCharacter oder null
     */
    getChar(x, y) {
        return this.cells.get(`${x},${y}`) || null
    }

    /**
     * Setzt ein Zeichen an einer Position.
     *
     * @param {number} x - Spalte
     * @param {number} y - Zeile
     * @param {string} char - Das Zeichen
     * @param {string} style - Style-Name aus Style enum
     */
    setChar(x, y, char, style = Style.MENU_ITEM) {
        if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) return

        this.cells.set(`${x},${y}`, createScreenCharacter(char, style))
        this.isDirty = true
        this.isActive = true
    }

    /**
     * Fügt einen proportionalen Text-Span hinzu.
     * Wird vom Renderer proportional oder monospace gerendert (über Grid-Zellen).
     *
     * @param {number} x - Start-Spalte (Character-Koordinate)
     * @param {number} y - Zeile (Character-Koordinate)
     * @param {string} text - Der Text
     * @param {string} style - Style-Name
     * @param {number} maxWidth - Max Breite in Zeichen (für Clipping)
     * @param {Object} [options] - Optionen: cursorPos, cursorVisible, selectionStart, selectionEnd, align
     */
    addTextSpan(x, y, text, style, maxWidth, options = {}) {
        this.textSpans.push({ x, y, text, style, maxWidth, ...options })
        this.isDirty = true
        this.isActive = true
    }

    /**
     * Leert den Overlay komplett.
     * Setzt isActive auf false, aber das wird von setChar wieder auf true gesetzt
     * wenn neue Inhalte gezeichnet werden.
     */
    clear() {
        this.cells.clear()
        this.textSpans = []
        this.frames = []
        this.stripes = []
        this.icons = []
        this.phosphorOverlay = null
        this.isDirty = true
        // isActive bleibt true wenn wir nur clearen um neu zu zeichnen
        // Es wird nur false wenn der Overlay wirklich geschlossen wird (via deactivate)
    }

    /**
     * Deaktiviert den Overlay komplett (schließt ihn).
     */
    deactivate() {
        this.cells.clear()
        this.textSpans = []
        this.frames = []
        this.stripes = []
        this.icons = []
        this.phosphorOverlay = null
        this.isDirty = true
        this.isActive = false
        // Konservativ: wir wissen nicht, ob das Overlay ueber Pane-Borders
        // gemalt hat — daher Border-Layer im naechsten Render neu zeichnen.
        // Der Render-Loop liest dieses Flag aus und cleart es.
        this.borderMayBeDirty = true
        // Modal-Bounds-Cache leeren — der naechste Modal-Open ist garantiert
        // "neu" und triggert den Border-Dirty-Sweep wieder.
        this._lastModalBounds.clear()
    }

    /**
     * Aktualisiert die Dimensionen (nach Screen-Resize).
     *
     * @param {number} cols - Neue Spaltenanzahl
     * @param {number} rows - Neue Zeilenanzahl
     */
    resize(cols, rows) {
        this.cols = cols
        this.rows = rows

        // Entferne Zellen die jetzt außerhalb liegen
        for (const key of this.cells.keys()) {
            const [x, y] = key.split(',').map(Number)
            if (x >= cols || y >= rows) {
                this.cells.delete(key)
            }
        }

        this.isDirty = true
    }

    /**
     * Zeichnet einen Menü-Rahmen mit Hintergrund + Border + optionalem
     * Title-Strip. Operiert im Proportional-Mode direkt als Canvas-Path/Fill
     * (Background-Rect bis zur Mitte der Border-Cells, Border-Linie auf der
     * Außenkante des Rects), im Mono-Mode auf charwidth-aligned Koordinaten
     * mit identischer Optik (volle Border-Cell-Fläche, Border auf
     * Cell-Grenzen). Siehe docs/menu-inventory-and-concept.md §4.
     *
     * Diese Primitive zeichnet NUR den Frame. Items/Inhalt malen die
     * Aufrufer mit `drawMenuItem`, `addTextSpan` oder `setChar`. Der Frame
     * stellt sicher, dass der Editor-Text unter dem Overlay blickdicht
     * ueberdeckt ist — pro-Cell-Background-Rects sind nicht mehr noetig.
     *
     * @param {Object} bounds - {x, y, width, height} in Grid-Koordinaten
     * @param {Object} [options]
     *   @param {string}  options.title         - Titel-Text (optional)
     *   @param {boolean} options.double        - Doppellinien-Border (rein semantisch, der Renderer zeichnet stets eine Single-Pixel-Linie)
     *   @param {string}  options.borderStyle   - default: MENU_BORDER
     *   @param {string}  options.bgStyle       - default: MENU_BACKGROUND
     *   @param {string}  options.titleStyle    - default: MENU_TITLE
     *   @param {string}  options.titleBgStyle  - default: MENU_TITLE_BG
     */
    drawMenuFrame(bounds, options = {}) {
        const {
            title = '',
            double = false,
            borderStyle = Style.MENU_BORDER,
            bgStyle = Style.MENU_BACKGROUND,
            titleStyle = Style.MENU_TITLE,
            titleBgStyle = Style.MENU_TITLE_BG
        } = options
        const { x, y, width, height } = bounds

        const titleText = title ? ` ${title} ` : ''
        const titleX = title ? x + 2 : null
        const titleLen = titleText.length

        this.frames.push({
            x, y, width, height,
            double,
            borderStyle,
            bgStyle,
            title,
            titleStyle,
            titleBgStyle,
            titleX,
            titleLen
        })

        // Title als Text-Span emittieren — im Prop-Mode rendert der Renderer
        // ihn proportional, im Mono-Mode ueber den Mono-Fallback. clearBackground
        // sorgt dafuer, dass der Border-Path unter dem Title pixelgenau und
        // blickdicht uebermalt wird (sonst sieht der Title „durchgestrichen"
        // aus oder Editor-Text scheint durch).
        if (title) {
            this.addTextSpan(titleX, y, titleText, titleStyle, titleLen, {
                clearBackground: true
            })
        }

        this.isDirty = true
        this.isActive = true
    }

    /**
     * Zeichnet ein Menü-Item nach der globalen Konvention:
     * - `[K]`-Bracket immer in Mono-Cells (auch im Prop-Mode, damit die
     *   Brackets sauber untereinander stehen)
     * - Label proportional via addTextSpan (im Mono-Mode wird der Span vom
     *   Renderer automatisch monospaced gerendert)
     * - Selected → vollflaechiger Stripe in MENU_ITEM_SELECTED_BG
     * - isCurrent → Checkmark ✓ rechts, hasSubmenu → Pfeil → ganz rechts
     * - isDisabled → PANE_INFO-Style ohne Stripe
     *
     * @param {number} x      - Grid-X (Start des Items, innerhalb des Frames)
     * @param {number} y      - Grid-Y
     * @param {Object} item
     *   @param {string}  item.key         - Single-char Shortcut (optional)
     *   @param {string}  item.label       - Anzeigetext (proportional)
     *   @param {boolean} item.hasSubmenu
     *   @param {boolean} item.isCurrent
     *   @param {boolean} item.isDisabled
     * @param {Object} [options]
     *   @param {boolean} options.selected  - aktiviert Stripe + Selected-Styles
     *   @param {number}  options.itemWidth - Gesamtbreite (fuer Stripe und rechtsbuendige Marker)
     */
    drawMenuItem(x, y, item, options = {}) {
        const { selected = false, itemWidth = 20 } = options

        // Disabled-Items: gedimmt, niemals selektiert, kein Stripe.
        if (item.isDisabled) {
            const label = item.label || ''
            this.addTextSpan(x + 4, y, label, Style.PANE_INFO, itemWidth - 4)
            this.isDirty = true
            this.isActive = true
            return
        }

        if (selected) {
            this.stripes.push({
                x, y,
                width: itemWidth,
                bgStyle: Style.MENU_ITEM_SELECTED_BG
            })
        }

        const keyStyle = selected ? Style.MENU_ITEM_KEY_SELECTED : Style.MENU_ITEM_KEY
        const labelStyle = selected ? Style.MENU_ITEM_SELECTED : Style.MENU_ITEM

        // [K] als Mono-Cells
        if (item.key) {
            this.setChar(x, y, '[', keyStyle)
            this.setChar(x + 1, y, item.key, keyStyle)
            this.setChar(x + 2, y, ']', keyStyle)
        }

        // Label: proportional im Prop-Mode (addTextSpan), Mono-Cells im
        // Mono-Mode. addTextSpan unterstuetzt beide ueber den Renderer.
        const labelX = item.key ? x + 4 : x
        const reserveRight = (item.isCurrent ? 2 : 0) + (item.hasSubmenu ? 2 : 0)
        const labelMaxWidth = Math.max(0, itemWidth - (labelX - x) - reserveRight)
        if (item.label) {
            this.addTextSpan(labelX, y, item.label, labelStyle, labelMaxWidth)
        }

        // Checkmark (✓) — links neben dem Submenu-Pfeil, sonst ganz rechts
        if (item.isCurrent) {
            const checkX = x + itemWidth - 1 - (item.hasSubmenu ? 2 : 1)
            this.setChar(checkX, y, '✓', labelStyle)
        }

        // Submenu-Pfeil (→) — ganz rechts
        if (item.hasSubmenu) {
            this.setChar(x + itemWidth - 2, y, '→', labelStyle)
        }

        this.isDirty = true
        this.isActive = true
    }

    /**
     * Schiebt einen Selection-Stripe (vollflaechiger Hintergrund-Streifen)
     * fuer ein einzelnes Item-Rechteck ins Overlay. Wird vom Context Menu
     * benutzt, das `drawMenuItem` nicht nutzen kann (mehrspaltiges Layout),
     * aber denselben Stripe-Look fuer Hover braucht.
     *
     * Geometrie identisch zu dem, was `drawMenuItem` intern emittiert.
     *
     * @param {number} x        - Grid-X (Start des Stripes)
     * @param {number} y        - Grid-Y
     * @param {number} width    - Stripe-Breite in Cells
     * @param {string} [bgStyle] - default: MENU_ITEM_SELECTED_BG
     */
    addItemStripe(x, y, width, bgStyle = Style.MENU_ITEM_SELECTED_BG) {
        this.stripes.push({ x, y, width, bgStyle })
        this.isDirty = true
        this.isActive = true
    }

    /**
     * Setzt `borderMayBeDirty = true` nur, wenn sich die Bounds eines Modals
     * seit dem letzten Aufruf mit demselben Key veraendert haben. Verhindert,
     * dass blinkende/hovernde Modals mit konstanter Geometrie den Border-Layer
     * pro Frame neu malen lassen.
     *
     * Beim Schliessen eines Modals immer `clearModalBounds(key)` rufen — der
     * naechste Open kommt damit garantiert in den "geaendert"-Pfad.
     *
     * @param {string} key   - Modal-Identifier (z.B. 'linkPicker', 'pathPicker')
     * @param {{x:number,y:number,width:number,height:number}} bounds
     */
    markBorderDirtyIfModalBoundsChanged(key, bounds) {
        const last = this._lastModalBounds.get(key)
        if (!last
                || last.x !== bounds.x
                || last.y !== bounds.y
                || last.width !== bounds.width
                || last.height !== bounds.height) {
            this.borderMayBeDirty = true
            this._lastModalBounds.set(key, {
                x: bounds.x, y: bounds.y, width: bounds.width, height: bounds.height
            })
        }
    }

    /**
     * Verwirft den Geometry-Cache eines Modals und setzt borderMayBeDirty —
     * Aufrufer bei "Modal schliesst" oder "Modal wechselt Mode".
     */
    clearModalBounds(key) {
        if (this._lastModalBounds.delete(key)) {
            this.borderMayBeDirty = true
        }
    }

    /**
     * Schiebt ein Circular-Control-Icon ins Overlay (Prop-Mode-Akzent fuer
     * das Context-Menu: Fold/Unfold/Break/Out/In/Lock).
     *
     * Das Icon wird in einem 2-Cell-Gutter zentriert und ist deutlich
     * kleiner als die Pane-Border-Controls — der Renderer skaliert den
     * Radius beim Zeichnen. Symbole stammen aus `_drawControlSymbol`:
     * `+ - = o ● x ! v < >`.
     *
     * @param {number} pxCenterX - Pixel-Mittelpunkt X (proportional praezise)
     * @param {number} gridY     - Zeile (Grid-Koordinate)
     * @param {string} symbol    - Symbol-Zeichen (siehe oben)
     * @param {string} [style]   - default: MENU_ITEM (Border + Symbol gleiche Farbe)
     */
    addControlIcon(pxCenterX, gridY, symbol, style = Style.MENU_ITEM) {
        this.icons.push({ pxCenterX, gridY, symbol, style })
        this.isDirty = true
        this.isActive = true
    }

    /**
     * Zeichnet den Link-Picker (Dropdown für Section-Link-Auswahl).
     *
     * @param {number} x - Position X (unter dem Cursor)
     * @param {number} y - Position Y
     * @param {Array<{display: string}>} items - Gefilterte Separator-Items
     * @param {number} selectedIndex - Aktuell selektierter Index
     * @param {string} filter - Aktueller Filter-Text
     * @returns {{width: number, height: number}}
     */
    drawLinkPicker(x, y, items, selectedIndex, filter, proportionalInfo = null, cursorBlinkOn = true, scrollOffset = 0) {
        // LinkPicker wechselt waehrend des Tippens seine Groesse (Item-Liste
        // wird gefiltert). Schrumpft er und lag vorher ueber einer Pane-Border,
        // bleiben Reste auf der Border-Linie stehen — der Render-Loop wischt
        // pro Frame nur die Pane-innerBounds, nicht persistente Border-Pixel.
        // Border-Layer wird daher dirty markiert, sobald sich Position oder
        // Groesse aendert (siehe markBorderDirtyIfModalBoundsChanged unten).
        const maxVisible = Math.min(items.length, EDITOR.LINK_PICKER_MAX_VISIBLE)
        const contentRows = Math.max(maxVisible, 1)
        const titleText = filter ? `@ ${filter}` : '@'
        const visibleItems = items.slice(0, Math.max(maxVisible, 1))

        // Breite — in Prop-Mode pixel-genau ueber das Glyph-Layout berechnen,
        // sonst char-count. Die Items sitzen eine Spalte weiter rechts als die
        // Eingabezeile (1 Cell Inset), darum brauchen sie +5 Rahmen+Padding,
        // die Eingabezeile +4.
        let width
        if (proportionalInfo) {
            const { metricsCache, scaleFactor, charWidth } = proportionalInfo
            const baseStep = metricsCache.baseStep
            const itemRunsFor = (s) => ([{
                start: 0, end: Array.from(s).length,
                bold: false, italic: false, underline: false, strikethrough: false,
                style: Style.MENU_ITEM
            }])
            const titleRuns = [{
                start: 0, end: Array.from(titleText).length,
                bold: false, italic: false, underline: false, strikethrough: false,
                style: Style.MENU_TITLE
            }]
            let maxItemPx = 0
            for (const item of visibleItems) {
                if (!item || !item.display) continue
                const layout = computeStringLayout(item.display, metricsCache, baseStep, itemRunsFor(item.display))
                const px = layout.totalWidth * scaleFactor
                if (px > maxItemPx) maxItemPx = px
            }
            const titleLayout = computeStringLayout(titleText, metricsCache, baseStep, titleRuns)
            const titlePx = titleLayout.totalWidth * scaleFactor
            const itemCols = Math.ceil(maxItemPx / charWidth)
            const titleCols = Math.ceil(titlePx / charWidth)
            width = Math.max(itemCols + 5, titleCols + 4, 16)
        } else {
            const maxLabelLen = items.length > 0
                ? Math.max(...visibleItems.map(i => i.display.length))
                : 10
            width = Math.max(maxLabelLen + 5, titleText.length + 4, 16)
        }

        // Layout: Top-Border + Eingabezeile + Trennlinie + Items + Bottom-Border.
        // Anders als frueher sitzt die Eingabe IN der Overlay-Flaeche (1. Innen-
        // zeile), darunter eine Trennlinie — konsistent mit dem Header+Separator-
        // Aufbau des SpellCheckMenu.
        const ITEM_ROW_OFFSET = 3  // Border(1) + Eingabezeile(1) + Trennlinie(1)
        const height = contentRows + ITEM_ROW_OFFSET + 1

        // Position clampen (Box nicht ueber den Rand hinaus, falls unten kein
        // Platz: ueber den Cursor schieben).
        const clampedX = Math.min(x, Math.max(0, this.cols - width))
        const clampedY = y + height > this.rows
            ? Math.max(0, y - height)
            : y

        // Frame via Standard-Primitive — ohne Title. Die Eingabezeile wird als
        // erste Innenzeile manuell gezeichnet, weil sie einen sichtbaren
        // Filter-Cursor braucht (drawMenuFrame unterstuetzt das nicht).
        this.drawMenuFrame(
            { x: clampedX, y: clampedY, width, height },
            { borderStyle: Style.MENU_BORDER, bgStyle: Style.MENU_BACKGROUND }
        )

        // Eingabezeile sitzt am 1-Cell-Frame-Inset, die Items eine Spalte
        // weiter rechts (bewusster Versatz — die Eingabe wirkt so wie ein
        // Prompt, die Items als eingerueckte Liste darunter).
        const inputX = clampedX + 1
        const itemX = clampedX + 2

        // Eingabezeile (1. Innenzeile): '@ filter' mit Filter-Cursor. Der Cursor
        // sitzt auf dem trailing Space direkt hinter dem Text. cursorBlinkOn
        // folgt der globalen Pane-Cursor-Blink-Phase, damit der Filter-Cursor
        // synchron mit dem Editor-Cursor blinkt.
        const inputText = `${titleText} `
        this.addTextSpan(inputX, clampedY + 1, inputText, Style.MENU_TITLE, width - 2, {
            cursorPos: titleText.length,
            cursorVisible: cursorBlinkOn
        })

        // Trennlinie (2. Innenzeile) — volle horizontale Linie, wie die Divider
        // im SpellCheckMenu und im Command-Palette-Submenu (`_drawMenu`).
        for (let c = 1; c < width - 1; c++) {
            this.setChar(clampedX + c, clampedY + 2, '─', Style.MENU_BORDER)
        }

        // Items ab der 3. Innenzeile — einheitliches Highlighting ueber
        // drawMenuItem: das selektierte Item bekommt den Selection-Stripe.
        // scrollStart kommt vom LinkPicker (Single Source of Truth fuer den
        // Scroll-Offset) — hier nur defensiv clampen, falls die Item-Zahl seit
        // dem letzten Frame geschrumpft ist (Filter-Race).
        const itemWidth = width - 3  // 1 Border + 1 Inset links, 1 Border rechts
        let scrollStart = 0

        if (items.length === 0) {
            this.addTextSpan(itemX, clampedY + ITEM_ROW_OFFSET, '(no matches)', Style.PANE_INFO, width - 4)
        } else {
            scrollStart = Math.max(0, Math.min(scrollOffset, items.length - maxVisible))
            for (let i = 0; i < maxVisible && scrollStart + i < items.length; i++) {
                const itemIdx = scrollStart + i
                const item = items[itemIdx]
                if (!item) continue  // Defensiv: items koennen waehrend Filter-Race sparse erscheinen
                const isSelected = itemIdx === selectedIndex
                this.drawMenuItem(
                    itemX, clampedY + ITEM_ROW_OFFSET + i,
                    {
                        key: null,
                        label: item.display,
                        hasSubmenu: false,
                        isCurrent: false,
                        isDisabled: false
                    },
                    { selected: isSelected, itemWidth }
                )
            }
        }

        this.markBorderDirtyIfModalBoundsChanged('linkPicker', {
            x: clampedX, y: clampedY, width, height
        })
        return { x: clampedX, y: clampedY, width, height, scrollStart, maxVisible, itemRowOffset: ITEM_ROW_OFFSET }
    }

    /**
     * Zeichnet eine zentrierte Phosphor-Box (Gimmick fuer About): schwarzer
     * Hintergrund, gruener Glow-Text — unabhaengig vom aktuellen DisplayMode.
     * Bypassed das Style-System: der Renderer rendert das Phosphor-Overlay
     * mit festen Farben direkt via Canvas-API (siehe Renderer._renderPhosphorOverlay).
     *
     * @param {Array<string>} lines - Textzeilen
     * @param {boolean} double - Doppellinien-Rahmen?
     * @returns {{x: number, y: number, width: number, height: number}}
     */
    drawPhosphorBox(lines, double = true) {
        const maxLineLength = Math.max(...lines.map(l => l.length))
        const width = maxLineLength + 4
        const height = lines.length + 2
        const x = Math.floor((this.cols - width) / 2)
        const y = Math.floor((this.rows - height) / 2)

        this.phosphorOverlay = { x, y, width, height, lines: lines.slice(), double }
        this.isDirty = true
        this.isActive = true

        return { x, y, width, height }
    }

    /**
     * Markiert den Overlay als clean (nach Rendering).
     */
    markClean() {
        this.isDirty = false
    }

    /**
     * Markiert den Overlay als dirty.
     */
    markDirty() {
        this.isDirty = true
    }
}

